import { Pipe, PipeTransform } from '@angular/core';

/**
 * MF-1881 — affichage de la liste des sujets d'une visite.
 *
 * Mutualisé parce que la même liste apparaît sur le détail, le compte rendu, les deux tableaux
 * de bord, les déplacements, le planning de semaine et le fil d'actualité : sans ce pipe, la
 * même troncature serait réécrite à chaque endroit.
 *
 * Deux modes, pilotés par le paramètre `max` :
 *
 *   max = 0 (défaut) : tous les sujets, séparés par une virgule, sans aucune troncature.
 *                      C'est le mode du détail d'une visite et du compte rendu, où la place
 *                      ne manque pas et où couper n'a pas de sens.
 *
 *   max > 0          : les `max` premiers sujets, puis « +X ». C'est le mode des cellules de
 *                      tableau et des cartes de tableau de bord. L'attribut `title` de
 *                      l'élément reçoit la liste complète, pour l'infobulle au survol.
 *
 * Une liste vide ou absente renvoie « - - - », la valeur attendue par le critère d'acceptation 6
 * du ticket. C'est volontaire : les écrans n'ont plus à tester eux-mêmes le cas de la visite
 * sans sujet, ce qui évite les `*ngIf="... == null"` qui ne fonctionnent plus avec un tableau
 * (en JavaScript, un tableau vide n'est ni null ni falsy).
 *
 * Utilisation :
 *
 *   <!-- liste compacte, libellé complet dans l'infobulle -->
 *   <span [title]="visit.visit_topic | topicList">{{ visit.visit_topic | topicList:2 }}</span>
 *
 *   <!-- affichage complet -->
 *   <span>{{ detail?.visit_topic | topicList }}</span>
 */
@Pipe({ name: 'topicList' })
export class TopicListPipe implements PipeTransform {

  /** Valeur affichée quand la visite ne porte aucun sujet. */
  static readonly EMPTY = '- - -';

  transform(topics: string[] | string | null | undefined, max: number = 0): string {
    const list = this.normalize(topics);

    if (list.length === 0) {
      return TopicListPipe.EMPTY;
    }
    if (max <= 0 || list.length <= max) {
      return list.join(', ');
    }
    return list.slice(0, max).join(', ') + ' +' + (list.length - max);
  }

  /**
   * MF-1881 — accepte encore une chaîne en plus d'un tableau.
   *
   * Protection délibérée pendant la bascule : si un écran reçoit encore l'ancienne forme du
   * back (sujet unique, chaîne), `join` lèverait une erreur et casserait tout l'écran, pas
   * seulement la cellule du sujet.
   */
  private normalize(topics: string[] | string | null | undefined): string[] {
    if (!topics) {
      return [];
    }
    if (typeof topics === 'string') {
      return topics.trim() === '' ? [] : [topics];
    }
    if (!Array.isArray(topics)) {
      return [];
    }
    return topics.filter(t => !!t && String(t).trim() !== '');
  }
}
